import { useMutation } from '@tanstack/react-query';
import { initializeApp } from 'firebase/app';
import {
  AuthError,
  AuthErrorCodes,
  getAuth,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  User,
} from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  FIREBASE_API_KEY,
  FIREBASE_APP_ID,
  FIREBASE_AUTH_DOMAIN,
  FIREBASE_DATABASE_URL,
  FIREBASE_MEASUREMENT_ID,
  FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_PROJECT_ID,
  FIREBASE_STORAGE_BUCKET,
} from 'src/utils/envConfig';

import { AuthContext } from '@/context/AuthContext';
import { TOASTTYPE, useToast } from '../../ui-components/Toast/Toast';

const firebaseConfig = {
  apiKey: FIREBASE_API_KEY,
  authDomain: FIREBASE_AUTH_DOMAIN,
  databaseURL: FIREBASE_DATABASE_URL,
  projectId: FIREBASE_PROJECT_ID,
  storageBucket: FIREBASE_STORAGE_BUCKET,
  messagingSenderId: FIREBASE_MESSAGING_SENDER_ID,
  appId: FIREBASE_APP_ID,
  measurementId: FIREBASE_MEASUREMENT_ID,
};

const firebase = initializeApp(firebaseConfig);

export const auth = getAuth(firebase);

export const AuthProvider = ({
  children,
}: {
  readonly children: React.ReactNode;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      // Listen for auth state changes
      onAuthStateChanged(auth, async (data) => {
        setUser(data);

        // If user is logged in and we're online, refresh the token
        if (data && navigator.onLine) {
          try {
            // Force token refresh
            const token = await data.getIdToken(true);

            // Make a request to ensure the token is cached by service worker
            await fetch('https://securetoken.googleapis.com/v1/token', {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
          } catch (error) {
            console.warn('Failed to refresh authentication token:', error);
          }
        }

        setIsLoading(false);
      });
    };

    initAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        isLoggedIn: isLoading ? null : Boolean(user),
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useLogin = () => {
  const intl = useIntl();
  const toast = useToast();
  const {
    mutateAsync: login,
    isPending: isLoading,
    error,
  } = useMutation({
    mutationFn: async (variables: {
      readonly email: string;
      readonly password: string;
    }) => signInWithEmailAndPassword(auth, variables.email, variables.password),

    onSuccess: () => {
      toast({
        subtitle: intl.formatMessage({
          defaultMessage: 'You have logged in!',
          id: 'qtEWgo',
        }),
        mode: TOASTTYPE.Success,
        icon: '🔓',
      });
    },

    onError: (responseError) => {
      const errorMessages: Record<string, string> = {
        [AuthErrorCodes.USER_DELETED]: intl.formatMessage({
          defaultMessage: 'This E-Mail is not registered!',
          id: 'zvxPVI',
        }),
        [AuthErrorCodes.INVALID_EMAIL]: intl.formatMessage({
          defaultMessage: 'The entered E-Mail is not a valid adress!',
          id: 'Egd/BO',
        }),
        [AuthErrorCodes.INVALID_PASSWORD]: intl.formatMessage({
          defaultMessage: 'Incorrect Password!',
          id: 'Vo54DO',
        }),
      };

      const matchingMessage = errorMessages[(responseError as AuthError)?.code];

      toast({
        title:
          matchingMessage ??
          intl.formatMessage({
            defaultMessage: 'An unknown error occured',
            id: 'n4Mcr2',
          }),
        mode: TOASTTYPE.Danger,
      });
    },
  });

  return {
    login,
    isLoading,
    error: (error as AuthError)?.code ?? null,
  };
};

export const useLogout = () => {
  const intl = useIntl();
  const toast = useToast();

  const { mutateAsync: logout, isPending: isLoading } = useMutation({
    mutationFn: () => signOut(auth),

    onSuccess: () => {
      toast({
        title: intl.formatMessage({
          defaultMessage: 'Logged out successfully!',
          id: '6CphAO',
        }),
        subtitle: intl.formatMessage({
          defaultMessage: 'See you soon!',
          id: 'XHCOc7',
        }),
        mode: TOASTTYPE.Success,
        icon: '👋🏻',
      });
    },
  });

  return {
    logout,
    isLoading,
  };
};

export const useSendPasswordResetEmail = () => {
  const intl = useIntl();
  const toast = useToast();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: (variables: { readonly email: string }) =>
      sendPasswordResetEmail(auth, variables.email),

    onSuccess: () => {
      toast({
        title: intl.formatMessage({
          defaultMessage: 'Instructions sent, check your E-Mail! ✉️',
          id: 'B3+etT',
        }),
        mode: TOASTTYPE.Info,
      });
    },

    onError: (responseError) => {
      const errorMessages: Record<string, string> = {
        [AuthErrorCodes.USER_DELETED]: intl.formatMessage({
          defaultMessage: 'This E-Mail is not registered!',
          id: 'zvxPVI',
        }),
        [AuthErrorCodes.INVALID_EMAIL]: intl.formatMessage({
          defaultMessage: 'The entered E-Mail is not a valid adress!',
          id: 'Egd/BO',
        }),
      };

      const matchingMessage = errorMessages[(responseError as AuthError)?.code];

      toast({
        title:
          matchingMessage ??
          intl.formatMessage({
            defaultMessage: 'An unknown error occured',
            id: 'n4Mcr2',
          }),
        mode: TOASTTYPE.Danger,
      });
    },
  });

  return {
    sendPasswordResetEmail: mutateAsync,
    isLoading,
  };
};
